import { environment } from '@env/environment';
import { EXTERNAL_SOURCE_ALIASES, ExternalSource } from '../models';
import type { AppRoles } from './app-roles.service';

/**
 * The one table describing the downstream apps Portal hands users off to.
 *
 * Every rule that keys off a handoff `source` reads it from here: the
 * origin allowlist that decides who may receive `?token=<accessToken>`, the
 * access check that decides whether the bounce-back is honoured, and the
 * per-app origin match. Before this module those lived as three separate
 * literals — `isAllowedExternalRedirect` in `app.component.ts`, and
 * `allowedForSource` plus an inline `hasAccessToSource` chain in
 * `home.component.ts` — and adding an app meant editing all of them. Miss
 * one and nothing errors: the user lands back on the dashboard with no
 * message, which is indistinguishable from the downstream app being broken.
 */
interface ExternalAppDescriptor {
  /** Configured base URL, empty where the app isn't deployed in this environment. */
  readonly url: string | undefined;
  /** The `AppRoles` key whose `hasAccess` gates the bounce-back. */
  readonly rolesKey: keyof AppRoles;
}

function externalApps(): Readonly<Record<ExternalSource, ExternalAppDescriptor>> {
  return {
    [ExternalSource.Colo]: { url: environment.onlineColoUrl, rolesKey: 'colo' },
    [ExternalSource.SiteLocator]: { url: environment.siteLocatorUrl, rolesKey: 'siteLocator' },
    [ExternalSource.VerticalIntel]: { url: environment.verticalIntelUrl, rolesKey: 'verticalIntel' },
    [ExternalSource.PartnerPortal]: { url: environment.partnerPortalUrl, rolesKey: 'partnerPortal' },
    [ExternalSource.VerticalWorks]: { url: environment.verticalWorksUrl, rolesKey: 'verticalWorks' },
  };
}

/**
 * Resolve a stored or query-string `source` to an `ExternalSource`.
 *
 * The comparison is case-insensitive and accepts the legacy aliases, because
 * the value arrives from another team's code: `sitelocator` is lower-case
 * where `verticalIntel` is camelCase, so a downstream author reading the
 * existing entries has no way to guess which convention applies to them. A
 * casing typo used to make the lookup `undefined` and drop the handoff
 * silently; now it survives.
 *
 * Returns null for anything not on the list.
 */
export function normalizeExternalSource(
  raw: string | null | undefined
): ExternalSource | null {
  if (!raw) {
    return null;
  }
  const lowered = raw.trim().toLowerCase();
  const match = Object.values(ExternalSource).find(s => s.toLowerCase() === lowered);
  return match ?? EXTERNAL_SOURCE_ALIASES[lowered] ?? null;
}

/** Configured base URL for a handoff source, or undefined if unknown/undeployed. */
export function externalAppUrlForSource(
  source: string | null | undefined
): string | undefined {
  const normalized = normalizeExternalSource(source);
  return normalized ? externalApps()[normalized].url : undefined;
}

function originOf(url: string): string | null {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

/**
 * Whether `url` points at a known VB app origin.
 *
 * This is the gate on delivering `?token=<accessToken>`. Without it a
 * crafted link `?source=colo&returnUrl=https://evil/phish` hands the user's
 * fresh access token to an attacker, so it must run at every point a
 * `returnUrl` is stored AND again before any redirect that appends the
 * token — storing and redirecting happen in different components, and a
 * gate on only one of them is no gate at all.
 */
export function isAllowedExternalRedirect(url: string): boolean {
  if (!url) {
    return false;
  }
  const target = originOf(url);
  if (!target) {
    return false;
  }
  return Object.values(externalApps())
    .map(app => (app.url ? originOf(app.url) : null))
    .some(origin => origin !== null && origin === target);
}

/**
 * Whether `url`'s origin is the one the declared `source` is supposed to
 * live at — a stricter check than `isAllowedExternalRedirect`, which only
 * asks whether the origin is a VB app at all.
 *
 * With no source, falls back to the historical Partner Portal handoff that
 * only ever set `redirectUrl`.
 */
export function externalRedirectMatchesSource(
  source: string | null | undefined,
  url: string
): boolean {
  const expected = source
    ? externalAppUrlForSource(source)
    : environment.partnerPortalUrl;
  if (!expected || !url) {
    return false;
  }
  const target = originOf(url);
  return target !== null && originOf(expected) === target;
}

/**
 * Whether the freshly-authenticated user actually has access to the app that
 * handed them off. Without a source, the legacy Partner Portal handoff
 * (`redirectUrl` only), held to the same rule as `source=partnerPortal`.
 */
export function hasAccessToExternalSource(
  source: string | null | undefined,
  roles: AppRoles
): boolean {
  const normalized = normalizeExternalSource(source);
  if (normalized) {
    return roles[externalApps()[normalized].rolesKey]?.hasAccess === true;
  }
  if (source) {
    // A source was declared but isn't one we know — no historical fallback
    // applies, so there is nothing to grant.
    return false;
  }
  return roles.partnerPortal.hasAccess === true;
}

/**
 * Whether a stored handoff is bound for Partner Portal: a declared
 * `partnerPortal` source, or no source and a PP-origin URL (the legacy
 * `redirectUrl` handoff, which LoginComponent stores without a source).
 *
 * PP handoffs are honoured only after the backend role check, because the
 * token can still grant PP to a user whose access request is pending. Both
 * PortalAuthService and HomeComponent decide on this one predicate.
 */
export function isPartnerPortalHandoff(
  source: string | null | undefined,
  url: string
): boolean {
  return source
    ? normalizeExternalSource(source) === ExternalSource.PartnerPortal
    : externalRedirectMatchesSource(null, url);
}

/**
 * Whether Vertical Works is deployed in this environment.
 *
 * One predicate rather than two switches for one fact. `verticalWorksUrl`
 * (the tile and the redirect allowlist) and `verticalWorksApiUrl` (the
 * status check and the access request) used to be tested independently, and
 * filling one but not the other failed silently in both directions: URL
 * only meant a visible tile whose status call never ran, so `hasAccess`
 * stayed false forever; API URL only meant a hidden tile that still fired a
 * role check on every dashboard load.
 */
export function isVerticalWorksConfigured(): boolean {
  return !!environment.verticalWorksUrl && !!environment.verticalWorksApiUrl;
}
